import React from "react";
import { Resource } from "react-admin";
import UserIcon from "@material-ui/icons/Group";
import RolesIcon from "@material-ui/icons/AssignmentInd";
import RecentActorsIcon from "@material-ui/icons/RecentActors";
import StorefrontIcon from "@material-ui/icons/Storefront";
import VideoLibraryIcon from "@material-ui/icons/VideoLibrary";
import { MonetizationOn } from "@material-ui/icons";
import UserList from "./components/Users/users";
import UserCreate from "./components/Users/create-user";
import UserEdit from "./components/Users/edit-user";
import RoleCreate from "./components/Roles/RoleCreate";
import RoleList from "./components/Roles/roles";
import RoleEdit from "./components/Roles/RoleEdit";
import CreateProduct from "./components/StaticProducts/create-product";
import ProductList from "./components/StaticProducts/product-list";
import EditProduct from "./components/StaticProducts/edit-product";
import AccountList from "./components/Accounts/AccountsList";
import { AccountEdit } from "./components/Accounts/AccountEdit";
import { AccountCreate } from "./components/Accounts/AccountCreate";
import StoreLocationList from "./components/Store-Location/StoreLocationList";
import { StoreLocationEdit } from "./components/Store-Location/StoreLocationEdit";
import { StoreLocationCreate } from "./components/Store-Location/StoreLocationCreate";
import ShippingList from "./components/Shipping/shipping-list";
import ShippingEdit from "./components/Shipping/edit-shipping";
import OrderList from "./components/Orders/orders";
import OrderShow from "./components/Orders/view-order";
import CategoryList from "./components/Categories/CategoriesList";
import CategoryEdit from "./components/Categories/CategoryEdit";
import CategoryCreate from "./components/Categories/CategoryCreate";
import DynamicProducts from "./components/DynamicProducts/product-list";
import CreateDynamicProduct from "./components/DynamicProducts/create-product";
import EditDynamicProduct from "./components/DynamicProducts/edit-product";
import StoresList from "./components/Stores/StoresList";
import { StoreEdit } from "./components/Stores/StoreEdit";
import { StoreCreate } from "./components/Stores/StoreCreate";
import ProductsList from "./components/Categories/ProductsList";
import ShopifyStoreList from "./components/Shopify-Store/ShopifyStoreList";
import { ShopifyStoreEdit } from "./components/Shopify-Store/ShopifyStoreEdit";
import { ShopifyStoreCreate } from "./components/Shopify-Store/ShopifyStoreCreate";
import MaterialList from "./components/Material-Data/MaterialDataList";
import { MaterialDataCreate } from "./components/Material-Data/MaterialDataCreate";
import { MaterialDataEdit } from "./components/Material-Data/MaterialDataEdit";
import ParentCategoriesList from "./components/ParentCategories/ParentCategoriesList";
import ParentCategoryCreate from "./components/ParentCategories/ParentCategoryCreate";
import ParentCategoryEdit from "./components/ParentCategories/ParentCategoryEdit";
import {
  PERMISSION_LIST_SIGNET,
  SignetEngravingRingMaterialsResources,
  SignetFinishesDataResources,
  SignetInlayFinishes,
  SignetRingDataResources,
  SignetRingMaterialResources,
  SignetSchoolResources,
  SignetSignetInlayResources,
  SignetSignetInlayTypes,
  SignetSignetOptionCategories,
  SignetSignetPricingDataResources,
  SignetNonSchoolCerekoteResources,
} from "./components/Signet/util";
import {
  LazyBulkUpload,
  LazyBestSellers,
  LazyCreateBestSeller,
  LazyEditBestSeller,
  LazyBestSellerShow,
  LazySortOrderList,
  LazyTrainingVideoList,
  LazyTrainingVideoCreate,
  LazyTrainingVideoEdit,
  LazyCustomPricing,
  LazyCustomPricingList,
  LazyCustomPricingEdit,
  LazyCollegiateEngravingList,
  LazyCollegiateEngravingEdit,
  LazyCollegiateEngravingCreate,
  LazyPreciousPricingList,
  LazyPreciousPricingEdit,
  LazyPreciousPricingCreate,
  LazyPreciousInlayPricingList,
  LazyPreciousInlayPricingEdit,
  LazyPreciousInlayPricingCreate,
  LazyInlayList,
  LazyInlayEdit,
  LazyDesignPatternList,
  LazyDesignPatternEdit,
  LazyDesignPatternCreate,
  LazyRingFinishList,
  LazyRingFinishCreate,
  LazyRingFinishEdit,
  LazyInlayFinishList,
  LazyInlayFinishCreate,
  LazyInlayFinishEdit,
  LazySleeveList,
  LazySleeveEdit,
  LazySignetShapeList,
  LazySignetShapeCreate,
  LazySignetShapeEdit,
  LazySignetSizeList,
  LazySignetSizeCreate,
  LazySignetSizeEdit,
  LazySignetShankList,
  LazySignetShankCreate,
  LazySignetShankEdit,
  LazySignetCerakoteList,
  LazySignetCerakoteCreate,
  LazySignetCerakoteEdit,
  LazySignetEngravingList,
  LazySignetEngravingCreate,
  LazySignetEngravingEdit,
  LazySignetSchoolCerakoteList,
  LazySignetSchoolCerakoteCreate,
  LazySignetSchoolCerakoteEdit,
  LazySignetSchoolList,
  LazySignetSchoolCreate,
  LazySignetSchoolEdit,
  LazySignetRingList,
  LazySignetRingCreate,
  LazySignetRingEdit,
  LazySignetPricingList,
  LazySignetPricingCreate,
  LazySignetPricingEdit,
  LazySignetInlayList,
  LazySignetInlayCreate,
  LazySignetInlayEdit,
  LazySimpleProductCategoryList,
  LazySimpleProductCategoryCreate,
  LazySimpleProductCategoryEdit,
  LazySimpleProductList,
  LazySimpleProductCreate,
  LazySimpleProductEdit,
  LazyComponentList,
  LazyComponentCreate,
  LazyComponentEdit,
} from "./lazyComponents";
import { hasSimpleProductsOnlyAccess } from "./components/Users/userRoleUtils";

const hasPermission = (permissionsList, permission) =>
  permissionsList.includes(permission);

const SIMPLE_PRODUCTS_RESOURCE_NAMES = [
  "simple-product-categories",
  "simple-products",
  "components",
  "stores",
  "me",
];

const getResourcesWithPermissions = (permissions) => {
  const permissionsList = permissions.split(",");

  const resources = [
    <Resource
      key="users"
      name="users"
      list={hasPermission(permissionsList, "view_users") ? UserList : null}
      create={hasPermission(permissionsList, "add_users") ? UserCreate : null}
      icon={UserIcon}
      edit={UserEdit}
    />,
    <Resource
      key="roles"
      name="roles"
      list={hasPermission(permissionsList, "view_roles") ? RoleList : null}
      create={hasPermission(permissionsList, "add_roles") ? RoleCreate : null}
      edit={RoleEdit}
      icon={RolesIcon}
    />,
    <Resource
      key="stores"
      name="stores"
      list={hasPermission(permissionsList, "view_stores") ? StoresList : null}
      edit={hasPermission(permissionsList, "edit_stores") ? StoreEdit : null}
      create={hasPermission(permissionsList, "add_stores") ? StoreCreate : null}
      icon={StorefrontIcon}
    />,
    <Resource
      key="builder-platform"
      name="builder-platform"
      list={
        hasPermission(permissionsList, "view_shopify_stores")
          ? ShopifyStoreList
          : null
      }
      edit={
        hasPermission(permissionsList, "edit_shopify_stores")
          ? ShopifyStoreEdit
          : null
      }
      create={
        hasPermission(permissionsList, "add_shopify_stores")
          ? ShopifyStoreCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key="material-data"
      name="material-data"
      list={
        hasPermission(permissionsList, "view_material_data")
          ? MaterialList
          : null
      }
      edit={
        hasPermission(permissionsList, "edit_material_data")
          ? MaterialDataEdit
          : null
      }
      create={
        hasPermission(permissionsList, "add_material_data")
          ? MaterialDataCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key="signet-shape"
      name="signet-shape"
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetShapeList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetShapeEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetShapeCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key="signet-size"
      name="signet-size"
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetSizeList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetSizeEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetSizeCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key="shank"
      name="shank"
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetShankList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetShankEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetShankCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key="cerakote-color"
      name="cerakote-color"
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetCerakoteList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetCerakoteEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetCerakoteCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key="signet-engravings"
      name="signet-engravings"
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetEngravingList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetEngravingEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetEngravingCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key="school-cerakote-colors"
      name="school-cerakote-colors"
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetSchoolCerakoteList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetSchoolCerakoteEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetSchoolCerakoteCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key={SignetSchoolResources}
      name={SignetSchoolResources}
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetSchoolList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetSchoolEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetSchoolCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key={SignetRingDataResources}
      name={SignetRingDataResources}
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetRingList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetRingEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetRingCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key={SignetSignetPricingDataResources}
      name={SignetSignetPricingDataResources}
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetPricingList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetPricingEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetPricingCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource
      key={SignetSignetInlayResources}
      name={SignetSignetInlayResources}
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazySignetInlayList
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazySignetInlayCreate
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazySignetInlayEdit
          : null
      }
    />,
    <Resource
      key="accounts"
      name="accounts"
      list={
        hasPermission(permissionsList, "view_accounts") ? AccountList : null
      }
      edit={
        hasPermission(permissionsList, "edit_accounts") ? AccountEdit : null
      }
      create={
        hasPermission(permissionsList, "add_accounts") ? AccountCreate : null
      }
      icon={RecentActorsIcon}
    />,
    <Resource
      key="store-locations"
      name="store-locations"
      list={
        hasPermission(permissionsList, "view_accounts")
          ? StoreLocationList
          : null
      }
      edit={
        hasPermission(permissionsList, "edit_accounts")
          ? StoreLocationEdit
          : null
      }
      create={
        hasPermission(permissionsList, "add_accounts")
          ? StoreLocationCreate
          : null
      }
      icon={RecentActorsIcon}
    />,
    <Resource
      key="collegiate-engravings"
      name="collegiate-engravings"
      list={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.view_signet_data)
          ? LazyCollegiateEngravingList
          : null
      }
      edit={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.edit_signet_data)
          ? LazyCollegiateEngravingEdit
          : null
      }
      create={
        hasPermission(permissionsList, PERMISSION_LIST_SIGNET.add_signet_data)
          ? LazyCollegiateEngravingCreate
          : null
      }
      icon={StorefrontIcon}
    />,
    <Resource key="custom-pricing/profiles" name="custom-pricing/profiles" />,
    <Resource
      key="custom-pricing/ring-materials"
      name="custom-pricing/ring-materials"
    />,
    <Resource key="custom-pricing/inlays" name="custom-pricing/inlays" />,
    <Resource key="custom-pricing/sleeves" name="custom-pricing/sleeves" />,
    <Resource key="accounts/types" name="accounts/types" />,
    <Resource key="getBestsellerVideoList" name="getBestsellerVideoList" />,
    <Resource
      key={SignetRingMaterialResources}
      name={SignetRingMaterialResources}
    />,
    <Resource
      key={SignetFinishesDataResources}
      name={SignetFinishesDataResources}
    />,
    <Resource
      key={SignetSignetOptionCategories}
      name={SignetSignetOptionCategories}
    />,
    <Resource key={SignetSignetInlayTypes} name={SignetSignetInlayTypes} />,
    <Resource key={SignetInlayFinishes} name={SignetInlayFinishes} />,
    <Resource
      key={SignetEngravingRingMaterialsResources}
      name={SignetEngravingRingMaterialsResources}
    />,
    <Resource
      key={SignetNonSchoolCerekoteResources}
      name={SignetNonSchoolCerekoteResources}
    />,
    <Resource key="attribute-types" name="attribute-types" />,
    <Resource key="accounts/parents" name="accounts/parents" />,
    <Resource key="me" name="me" />,
    <Resource key="permissions" name="permissions" />,
    <Resource key="related-material-list" name="related-material-list" />,
    <Resource
      key="direct/orders"
      name="direct/orders"
      list={hasPermission(permissionsList, "view_orders") ? OrderList : null}
      show={hasPermission(permissionsList, "view_orders") ? OrderShow : null}
    />,
    <Resource
      key="store/orders"
      name="store/orders"
      list={hasPermission(permissionsList, "view_orders") ? OrderList : null}
      show={hasPermission(permissionsList, "view_orders") ? OrderShow : null}
    />,
    <Resource
      key="products"
      name="products"
      list={
        hasPermission(permissionsList, "view_products") ? ProductList : null
      }
      create={
        hasPermission(permissionsList, "add_products") ? CreateProduct : null
      }
      edit={
        hasPermission(permissionsList, "edit_products") ? EditProduct : null
      }
    />,
    <Resource
      key="dynamic/products"
      name="dynamic/products"
      options={{ label: "Customizable Products" }}
      list={
        hasPermission(permissionsList, "view_dynamic-products")
          ? DynamicProducts
          : null
      }
      create={
        hasPermission(permissionsList, "add_dynamic-products")
          ? CreateDynamicProduct
          : null
      }
      edit={
        hasPermission(permissionsList, "edit_dynamic-products")
          ? EditDynamicProduct
          : null
      }
    />,
    <Resource
      key="bestsellers"
      name="bestsellers"
      options={{ label: "Customizable Products" }}
      list={LazyBestSellers}
      create={LazyCreateBestSeller}
      edit={LazyEditBestSeller}
      show={LazyBestSellerShow}
    />,
    <Resource
      key="parent-categories"
      name="parent-categories"
      list={
        hasPermission(permissionsList, "view_categories")
          ? ParentCategoriesList
          : null
      }
      edit={
        hasPermission(permissionsList, "edit_categories")
          ? ParentCategoryEdit
          : null
      }
      create={
        hasPermission(permissionsList, "add_categories")
          ? ParentCategoryCreate
          : null
      }
    />,
    <Resource
      key="categories"
      name="categories"
      list={
        hasPermission(permissionsList, "view_categories") ? CategoryList : null
      }
      edit={
        hasPermission(permissionsList, "edit_categories") ? CategoryEdit : null
      }
      create={
        hasPermission(permissionsList, "add_categories") ? CategoryCreate : null
      }
    />,
    <Resource key="position" name="position" list={ProductsList} />,
    <Resource key="products/categories" name="products/categories" />,
    <Resource
      key="account/shipping"
      name="account/shipping"
      list={
        hasPermission(permissionsList, "view_shipping") ? ShippingList : null
      }
      edit={
        hasPermission(permissionsList, "edit_shipping") ? ShippingEdit : null
      }
    />,
    <Resource
      key="shipping"
      name="shipping"
      list={
        hasPermission(permissionsList, "view_shipping") ? ShippingList : null
      }
      edit={
        hasPermission(permissionsList, "edit_shipping") ? ShippingEdit : null
      }
    />,
    <Resource key="export/sample" name="export/sample" />,
    <Resource key="import/status" name="import/status" />,
    <Resource key="website/metrics" name="website/metrics" />,
    <Resource
      key="bulk-upload"
      name="bulk-upload"
      list={
        hasPermission(permissionsList, "view_users") ? LazyBulkUpload : null
      }
      create={
        hasPermission(permissionsList, "add_users") ? LazyBulkUpload : null
      }
      icon={UserIcon}
    />,
    <Resource
      key="nc-rings"
      name="nc-rings"
      list={
        hasPermission(permissionsList, "view_users")
          ? LazyCustomPricingList
          : null
      }
      create={
        hasPermission(permissionsList, "view_users") ? LazyCustomPricing : null
      }
      edit={
        hasPermission(permissionsList, "view_users")
          ? LazyCustomPricingEdit
          : null
      }
      icon={MonetizationOn}
    />,
    <Resource
      key="netsuite/inlay"
      name="netsuite/inlay"
      list={LazyInlayList}
      edit={LazyInlayEdit}
    />,
    <Resource
      key="netsuite/sleeve"
      name="netsuite/sleeve"
      list={LazySleeveList}
      edit={LazySleeveEdit}
    />,
    <Resource
      key="netsuite/design-pattern"
      name="netsuite/design-pattern"
      list={LazyDesignPatternList}
      edit={LazyDesignPatternEdit}
      create={LazyDesignPatternCreate}
    />,
    <Resource
      key="netsuite/ring-finish"
      name="netsuite/ring-finish"
      list={LazyRingFinishList}
      edit={LazyRingFinishEdit}
      create={LazyRingFinishCreate}
    />,
    <Resource
      key="netsuite/inlay-finish"
      name="netsuite/inlay-finish"
      list={LazyInlayFinishList}
      edit={LazyInlayFinishEdit}
      create={LazyInlayFinishCreate}
    />,
    <Resource
      key="ring-material-sort-order"
      name="ring-material-sort-order"
      list={LazySortOrderList}
    />,
    <Resource
      key="precious-price"
      name="precious-price"
      options={{ label: "Precious Pricing" }}
      list={
        hasPermission(permissionsList, "view_import_export")
          ? LazyPreciousPricingList
          : null
      }
      edit={
        hasPermission(permissionsList, "view_import_export")
          ? LazyPreciousPricingEdit
          : null
      }
      create={
        hasPermission(permissionsList, "view_import_export")
          ? LazyPreciousPricingCreate
          : null
      }
      icon={MonetizationOn}
    />,
    <Resource
      key="training-videos"
      name="training-videos"
      options={{ label: "Training Videos" }}
      list={LazyTrainingVideoList}
      create={LazyTrainingVideoCreate}
      edit={LazyTrainingVideoEdit}
      icon={VideoLibraryIcon}
    />,
    <Resource
      key="simple-product-categories"
      name="simple-product-categories"
      options={{ label: "Categories" }}
      list={LazySimpleProductCategoryList}
      create={LazySimpleProductCategoryCreate}
      edit={LazySimpleProductCategoryEdit}
    />,
    <Resource
      key="simple-products"
      name="simple-products"
      options={{ label: "Products" }}
      list={LazySimpleProductList}
      create={LazySimpleProductCreate}
      edit={LazySimpleProductEdit}
    />,
    <Resource
      key="components"
      name="components"
      options={{ label: "Components" }}
      list={LazyComponentList}
      create={LazyComponentCreate}
      edit={LazyComponentEdit}
    />,
    <Resource
      key="precious-inlay-pricing"
      name="precious-inlay-pricing"
      options={{ label: "Precious Inlay Pricing" }}
      list={
        hasPermission(permissionsList, "view_import_export")
          ? LazyPreciousInlayPricingList
          : null
      }
      edit={
        hasPermission(permissionsList, "view_import_export")
          ? LazyPreciousInlayPricingEdit
          : null
      }
      create={
        hasPermission(permissionsList, "view_import_export")
          ? LazyPreciousInlayPricingCreate
          : null
      }
      icon={MonetizationOn}
    />,
  ];

  if (hasSimpleProductsOnlyAccess(permissionsList)) {
    return resources.filter((resource) =>
      SIMPLE_PRODUCTS_RESOURCE_NAMES.includes(resource.props.name),
    );
  }

  return resources;
};

export default getResourcesWithPermissions;
